import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MaskApplierService } from 'ngx-mask';
import {
  AT_LEAST_8_CHAR_PATTERN, EMAIL_PATTERN,
  INCLUDE_SPECIAL_CHAR_PATTERN, SPECIAL_CHAR_PATTERN,
  UUID_PATTERN,
} from 'src/app/shared/constants';
import {
  EMAIL_INPUT, PASSWORD_INPUT, TELEPHONE_INPUT,
  MINIMUM_MESSAGE, MAXIMUM_MESSAGE, EMAIL_MESSAGE, NO_ONLY_SPACE_MESSAGE,
  INCLUDE_SPECIAL_CHAR_MESSAGE, AT_LEAST_8_CHAR_MESSAGE, SPECIAL_CHAR_MESSAGE, INCLUDE_UPPER_LOWER_CASE_CHAR_NUMBER_MESSAGE,
  UUID_MESSAGE
} from './input.constants';
import { AlertService, ToastTypes } from 'src/app/shared/services/alert.service';
import { noOnlySpacesValidator } from 'src/app/shared/validators/no-space.validator';
import { ClipboardService } from '../../../services/clipboard/ngx-clipboard.service';
import { LaunchDarklyService } from 'src/app/core/services/launchdarkly.service';
import { FEATURE_FLAGS } from 'src/app/shared/launchdarkly.constants';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss', '../form-fields.component.scss'],
})
export class InputComponent implements OnInit, OnDestroy {
  @Input('form') form!: UntypedFormGroup;
  @Input('label') label!: string;
  @Input('infoText') infoText!: string;
  @Input('labelHelper') labelHelper!: string;
  @Input('placeholder') placeholder: string = 'Enter here';
  @Input('type') type: string = 'text';
  @Input('requiredMessage') requiredMessage: string = 'This field is required';
  @Input('invalidMessage') invalidMessage: string = 'This field is invalid';
  @Input('matchMessage') matchMessage: string = 'This field does not match pattern';
  @Input('maxlengthMessage') maxlengthMessage: string = 'This field is too long';
  @Input('minlengthMessage') minlengthMessage: string = 'This field is too short';
  @Input('mininumMessage') mininumMessage:string = 'Value less than minimum value';
  @Input('maximumMessage') maximumMessage:string = 'Value greater than maximum value';
  @Input('controlName') controlName: string = '';
  @Input('disabled') disabled: boolean = false;
  @Input('mask') mask: string = '';
  @Input('prefix') prefix: string = '';
  @Input('piiData') piiData: any;
  @Input() requiredMark: boolean = false;
  @Input() isRequired: boolean = false;
  @Input('isHeader') isHeader: boolean = false;
  @Input('IsFormFieldNameHidden') IsFormFieldNameHidden = false;
  @Input('CustomFileInput') CustomFileInput = false;
  @Input('FileExtensions') FileExtensions: any;
  @Input('IsFromForm') IsFromForm: boolean = false;
  @Input('IsInfoVisible') IsInfoVisible: boolean = true;
  @Input('DisplayOnScreen') DisplayOnScreen: boolean = false;
  @Input('AllowMinimize') AllowMinimize: boolean = false;
  @Input('IsRowDisplayOnScreen') IsRowDisplayOnScreen: boolean = false;
  @Input('isPrintingForm') isPrintingForm: boolean = false;
  @Input('AllowCopy') AllowCopy: boolean = false;
  @Input('fieldSize') fieldSize: number = 0;
  @Input('PIIDecrypted') PIIDecrypted: any;
  @Input('fileValue') fileValue: string = '';
  @Input('isCsvFileInput') isCsvFileInput: boolean = false
  @Input('autocomplete') autocomplete: string = '';
  @Input('ariaDescribedby') ariaDescribedby: string = '';
  @Input('controlValue') controlValue: any;
  @Input('errorMessagePrefix') errorMessagePrefix: string = '';
  @Output('fileChange') fileChange = new EventEmitter<any>();

  isTextVisible: boolean = true
  PIIData: any = {};
  required = false;
  isAuthAccessibilityEnabled: boolean = false;
  isA11yFileInputEnabled: boolean = false;
  isReadonlyFileAttachmentFixEnabled: boolean = false;
  private flagSub: any;
  private a11yFileInputFlagSub: any;
  private readonlyFileAttachmentFixSub: any;
  acceptableFileTypes: any = '';
  selectedFile: any = '';
  validator = Validators.required;
  originalEncyptedValue: any;
  inputId: string = '';

  constructor(
    private clipboardService: ClipboardService,
    private alert: AlertService,
    private maskService: MaskApplierService,
    private readonly ldService: LaunchDarklyService
  ) { }

  ngOnInit(): void {
    this.flagSub = this.ldService.getFlagValue(FEATURE_FLAGS.ENABLE_AUTH_ACCESSIBILITY, false)
      .subscribe((value) => { this.isAuthAccessibilityEnabled = !!value; });
    this.a11yFileInputFlagSub = this.ldService.getFlagValue(FEATURE_FLAGS.ENABLE_A11Y_FILE_INPUT, false)
      .subscribe((value) => { this.isA11yFileInputEnabled = !!value; });
    this.readonlyFileAttachmentFixSub = this.ldService.getFlagValue(FEATURE_FLAGS.ENABLE_READONLY_FILE_ATTACHMENT_FIX, false)
      .subscribe((value) => {
        this.isReadonlyFileAttachmentFixEnabled = !!value;
        this.syncFileAttachmentReadonlyControlState();
      });
    // Generate unique ID for this input for label association
    this.inputId = `input-${this.controlName}-${Math.random().toString(36).substr(2, 9)}`;

    if (this.control?.errors?.required === true) this.required = true;
    if (this.type == 'email') {
      this.control?.addValidators([Validators.email, Validators.pattern(EMAIL_PATTERN)]);
    }
    // if(this.type == 'number'){
    //   this.control?.addValidators(Validators.pattern(/^-?\d*$/));
    // }
    if (this.PIIDecrypted) {
      this.originalEncyptedValue = this.control.value;
    }
    if (this.type === 'text' && (typeof this.control?.value === 'string' || !this.control?.value) && this.control?.hasValidator(Validators.required)) {
      this.control.addValidators(noOnlySpacesValidator())
    }
    if (this.fieldSize) {
      this.control?.addValidators([Validators.maxLength(this.fieldSize)]);
    }
    this.control?.updateValueAndValidity();

    setTimeout(() => {
      if (this.disabled) {
        this.required = false;
      }
    }, 0);

    if (this.FileExtensions && this.FileExtensions.length) {
      this.acceptableFileTypes = this.normalizeAcceptAttribute(this.FileExtensions);
    }
    setTimeout(() => this.syncFileAttachmentReadonlyControlState(), 0);
  }

  ngOnDestroy(): void {
    this.flagSub?.unsubscribe?.();
    this.a11yFileInputFlagSub?.unsubscribe?.();
    this.readonlyFileAttachmentFixSub?.unsubscribe?.();
  }

  ngOnChanges(changes: SimpleChanges) {
    const disabled = changes?.disabled?.currentValue;
    const isHeader = changes?.isHeader?.currentValue;
    if (this.control?.errors?.required === true) this.required = true;
    if (disabled === true || isHeader === true) {
      this.required = false;
      if (this.CustomFileInput && this.type === 'file') {
        this.syncFileAttachmentReadonlyControlState();
      } else {
        this.control?.disable();
      }
    } else if (disabled === false && !this.isHeader) {
      this.control?.enable();
      if (this.isRequired) {
        this.control?.addValidators(Validators.required);
        this.control?.updateValueAndValidity();
      }
    }
    if (this.type == 'email') {
      this.control?.addValidators([Validators.email, Validators.pattern(EMAIL_PATTERN)]);
    }
    if (this.type === 'text' && (typeof this.control?.value === 'string' || !this.control?.value) && this.control?.hasValidator(Validators.required)) {
      this.control.addValidators(noOnlySpacesValidator())
    }
    // if(this.type == 'number'){
    //   this.control?.addValidators(Validators.pattern(/^-?\d*$/));
    // }
    if (this.fieldSize) {
      this.control?.addValidators([Validators.maxLength(this.fieldSize)]);
    }
    this.control?.updateValueAndValidity();
    if (changes?.fileValue?.currentValue !== changes?.fileValue?.previousValue && !changes?.fileValue?.currentValue) {
      this.selectedFile = '';
    }
    if (changes.PIIDecrypted?.currentValue) {
      this.originalEncyptedValue = this.control.value;
    }
    if (changes?.FileExtensions?.currentValue) {
      this.acceptableFileTypes = this.normalizeAcceptAttribute(this.FileExtensions);
    }
    if (
      this.CustomFileInput &&
      this.type === 'file' &&
      (changes['disabled'] || changes['CustomFileInput'] || changes['isHeader']) &&
      (this.disabled || this.isHeader)
    ) {
      this.syncFileAttachmentReadonlyControlState();
    }
  }

  private syncFileAttachmentReadonlyControlState(): void {
    if (!this.CustomFileInput || this.type !== 'file' || !this.control) return;
    if (this.isHeader) {
      this.control.disable({ emitEvent: false });
      return;
    }
    if (!this.disabled) return;
    if (this.isReadonlyFileAttachmentFixEnabled) {
      this.control.disable({ emitEvent: false });
    } else {
      this.control.enable({ emitEvent: false });
    }
  }

  get control() {
    return this.form?.controls?.[this.controlName];
  }

  get numberFormControl() {
    return this.form?.controls?.[this.controlName] as UntypedFormControl;
  }

  get isPasswordType(): boolean {
    return this.type?.toLowerCase() === 'password';
  }

  showPassword = (el: HTMLInputElement) => {
    if (el.type === 'password') {
      el.type = 'text';
    }
  };

  hidePassword = (el: HTMLInputElement) => {
    if (this.isPasswordType) {
      el.type = 'password';
    }
  };

  togglePasswordVisibility = (el: HTMLInputElement): void => {
    el.type = el.type === 'password' ? 'text' : 'password';
  };

  showPIIData() {
    if (this.PIIDecrypted != null) {
      if (
        this.PIIDecrypted == this.form?.controls?.['Value']?.value &&
        this.PIIData[this.piiData.Name]
      ) {
        this.form?.controls?.['Value']?.setValue(this.PIIData[this.piiData.Name])
      } else {
        this.PIIData[this.piiData.Name] = this.form?.controls?.['Value']?.value;
        this.form?.controls?.['Value']?.setValue(this.PIIDecrypted)
      }
    }
  }

  /**
   * Set errors here priority wise
   * @return error message
   */
  getErrorMessage = () => {
    if (this.control?.hasError('mustMatch')) return this.matchMessage;
    if (this.control?.hasError('noOnlySpaces')) return NO_ONLY_SPACE_MESSAGE;
    if (this.control?.hasError('maxlength')) return this.maxlengthMessage;
    if (this.control?.hasError('minlength')) return this.minlengthMessage;
    if (this.control?.hasError('pattern')) {
      if (this.type === TELEPHONE_INPUT.type || this.controlName === TELEPHONE_INPUT.controlName) {
        return this.matchMessage;
      }
      if (this.type === EMAIL_INPUT.type || this.controlName === EMAIL_INPUT.controlName) {
        return EMAIL_MESSAGE;
      }
      if (this.type === PASSWORD_INPUT.type || this.controlName === PASSWORD_INPUT.controlName) {
        return this.getPasswordPatternErrorMessage();
      }
      if (this.control?.errors?.pattern?.requiredPattern === UUID_PATTERN.toString()) {
        return UUID_MESSAGE;
      }
    }
    if (this.control?.hasError('min')) return MINIMUM_MESSAGE;
    if (this.control?.hasError('max')) return MAXIMUM_MESSAGE;
    if (this.control?.hasError('email')) return EMAIL_MESSAGE;
    if (!this.control?.hasError('required')) return this.invalidMessage;
    return this.requiredMessage;
  };

  /**
   * get Password Pattern Error Message
   */
  getPasswordPatternErrorMessage = () => {
    if (this.control?.errors?.pattern?.requiredPattern === SPECIAL_CHAR_PATTERN.toString()) {
      return SPECIAL_CHAR_MESSAGE;
    } else if (this.control?.errors?.pattern?.requiredPattern === AT_LEAST_8_CHAR_PATTERN.toString()) {
      return AT_LEAST_8_CHAR_MESSAGE;
    } else if (this.control?.errors?.pattern?.requiredPattern === INCLUDE_SPECIAL_CHAR_PATTERN.toString()) {
      return INCLUDE_SPECIAL_CHAR_MESSAGE;
    } else {
      return INCLUDE_UPPER_LOWER_CASE_CHAR_NUMBER_MESSAGE;
    }
  }

  /**
   * Set value for PII field according to change on focus
   */
  onInputFocus = () => {
    if (this.piiData != null && (this.originalEncyptedValue)) {
      if (
        this.PIIData[this.piiData.Name] == undefined ||
        this.PIIData[this.piiData.Name] == this.form?.controls?.['Value']?.value
      ) {
        this.PIIData[this.piiData.Name] = this.form?.controls?.['Value']?.value;
        this.form?.controls?.['Value']?.setValue('');
      }
      if (this.PIIDecrypted == this.form?.controls?.['Value']?.value) {
        this.form?.controls?.['Value']?.setValue('');
      }
    }
  };

  /**
   * Set value for PII field according to change on blur
   */
  onInputBlur = () => {
    if (this.piiData != null && (this.originalEncyptedValue)) {
      if (
        this.PIIData[this.piiData.Name] != undefined &&
        (this.form?.controls?.['Value']?.value == '' || this.form.controls?.['Value']?.value == this.PIIDecrypted)
      ) {
        this.form.controls?.['Value']?.setValue(this.PIIData[this.piiData.Name]);
      }
    }
  };

  toggleText(): void {
    this.isTextVisible = !this.isTextVisible;
  }

  copy(value: any) {
    if (this.clipboardService.copyFromContent(value)) {
      this.alert.toast(
        ToastTypes.INFO,
        '',
        'Text Copied Successfully'
      )
    } else {
      this.alert.toast(
        ToastTypes.ERROR,
        '',
        'Failed to copy text'
      )
    }
  }

  isDoubleDashValidationAdded: boolean = false;

  setControlValue(event: any) {
    if ((this.type === 'date' || this.type === 'datetime-local') && this.control) {
      let date;
      if (event.target.value) {
        date = new Date(event.target.value);
      }
      // On keyup, only clear errors when value is valid — don't set errors until change/blur
      if (date && date.getFullYear() >= 1900 && !event.target.validationMessage) {
        this.control?.setErrors(null);
      }
    }
    else if (this.type === 'number' && this.control) {
      let value;
      let validationMessage;

      if (event) {
        value = event.target.value;
        validationMessage = event.target.validationMessage;
      }
      if (event?.key === "e" || event?.key === "+") {
        event.preventDefault();
      }
      if (value?.length > 0 && event?.key === "-") {
        event.preventDefault();
      }
      if (validationMessage && !value) {
        this.control?.setErrors({
          customError: true
        });
        this.control?.markAsDirty();
        this.isDoubleDashValidationAdded = true;
      } else if (event?.target?.value && this.isDoubleDashValidationAdded && this.control?.hasError('customError') && this.control.errors?.length === 1) {
        this.control?.setErrors(null);
        this.isDoubleDashValidationAdded = false;
      }
    }
  }

  /**
   * Validate date/datetime-local fields on change (fires on blur for keyboard entry,
   * immediately on date picker selection). This prevents showing errors while the user
   * is still typing a partial date.
   */
  validateDateField(event: any) {
    if ((this.type === 'date' || this.type === 'datetime-local') && this.control) {
      const value = event.target.value;
      let year: number | null = null;
      if (value) {
        const parsed = parseInt(value.split('-')[0], 10);
        if (!isNaN(parsed)) {
          year = parsed;
        }
      }
      const isValid = year !== null && year >= 1900 && !event.target.validationMessage;

      if (isValid) {
        this.control?.setErrors(null);
      } else if (event.type === 'blur') {
        if (year !== null && year < 1900) {
          this.control?.setErrors({ customError: true });
          this.control?.markAsDirty();
        } else if (event.target.validationMessage) {
          this.control?.setErrors({ customError: true });
          this.control?.markAsDirty();
        }
      }
    }
  }

  getFormControlValue() {
    if (this.mask && this.control?.value) {
      return this.maskService.applyMask(this.control?.value, this.mask);
    }
    return this.control?.value || '';
  }

  fileChanged(event: any) {
    if (this.disabled && this.isReadonlyFileAttachmentFixEnabled) return;
    if (event?.target?.files?.length > 0) {
      const file = event.target.files[0];

      // Validate file extension if FileExtensions is specified
      if (this.FileExtensions && file.name) {
        const allowedExtensions = this.FileExtensions
          .replace(/;/g, ',')
          .split(',')
          .map((ext: string) => ext.trim().toLowerCase())
          .filter((ext: string) => ext)
          .map((ext: string) => ext.startsWith('.') ? ext : `.${ext}`);

        const fileName = file.name.toLowerCase();
        const isValidExtension = allowedExtensions.some((ext: string) => fileName.endsWith(ext));

        if (!isValidExtension) {
          // Clear the input and reset control value
          event.target.value = '';
          this.selectedFile = '';
          if (this.control) {
            this.control.setValue(null);
            this.control.markAsTouched();
          }

          // Show error message
          const extensionsList = allowedExtensions.join(', ');
          this.alert.toast(
            ToastTypes.ERROR,
            'Invalid File Type',
            `Please select a file with one of the following extensions: ${extensionsList}`
          );
          return;
        }
      }

      // Set the control value to the file name for display
      if (this.control && file.name) {
        this.control.setValue(file.name);
        this.control.markAsTouched();
      }

      this.fileChange.emit(event);
    }
  }

  isEyeButtonVisisble() {
    return this.PIIDecrypted && this.PIIDecrypted !== this.originalEncyptedValue && (!this.control.value || this.control.value === this.PIIDecrypted || this.control.value === this.originalEncyptedValue)
  }

  getMinValue() {
    if (this.type === 'date') return '1900-01-01';
    else if (this.type === 'datetime-local') return '1900-01-01T00:00';
    else return null;
  }
  getMaxValue() {
    if (this.type === 'date') return '9999-12-31';
    else if (this.type === 'datetime-local') return '9999-12-31T23:59';
    else return null;
  }

  /**
   * Normalize the accept attribute value for file input
   * @param fileExtensions - String with file extensions (e.g., ".csv" or ".csv;.xlsx")
   * @returns Normalized accept attribute value with file extensions
   */
  normalizeAcceptAttribute(fileExtensions: string): string {
    if (!fileExtensions) return '';

    // Replace semicolons with commas for multiple extensions
    const extensions = fileExtensions.replace(/;/g, ',').split(',').map((ext: string) => ext.trim()).filter((ext: string) => ext);

    // Use extensions only in accept attribute for better cross-browser compatibility
    // File extension validation is done in JavaScript after file selection
    const acceptParts: string[] = [];

    extensions.forEach((ext: string) => {
      const normalizedExt = ext.startsWith('.') ? ext : `.${ext}`;
      const normalizedExtLower = normalizedExt.toLowerCase();

      // Use only extensions - Firefox handles this most reliably
      // JavaScript validation will check file extension after selection
      if (!acceptParts.includes(normalizedExtLower)) {
        acceptParts.push(normalizedExtLower);
      }
    });

    return acceptParts.join(',');
  }

  /**
   * Check if control has errors for ARIA invalid attribute
   * @returns true if control is dirty or has value and has errors
   */
  hasErrors(): boolean {
    if (this.isA11yFileInputEnabled && this.CustomFileInput) {
      return ((this.control?.dirty || this.control?.touched) && this.control?.errors) || (this.control?.value && this.control?.errors) || false;
    }
    return (this.control?.dirty && this.control?.errors) || (this.control?.value && this.control?.errors) || false;
  }

  /**
   * Get error message ID for aria-describedby
   * @returns error message ID string
   */
  getErrorId(): string {
    return `${this.inputId}-error`;
  }

  /**
   * Get complete aria-describedby value including error ID when errors exist
   * @returns space-separated string of IDs for aria-describedby
   */
  getAriaDescribedby(): string | null {
    const ids: string[] = [];

    // Add custom aria-describedby if provided
    if (this.ariaDescribedby) {
      ids.push(this.ariaDescribedby);
    }

    // Add error ID if control has errors
    if (this.hasErrors()) {
      ids.push(this.getErrorId());
    }

    return ids.length > 0 ? ids.join(' ') : null;
  }
}
